
import { ContentModel, IContentModel } from './ContentModel';
import { FetchingState } from '../Fetching/FetchingState';
import { GET_CONTENT } from '../../queries/Content';
import { captureException } from '../../../utils/sentry/sentry';
import { clientFunction } from '../../../config/apollo/client';
import { filterDefinedFields } from '../../../utils/filterFields';
import { flow, getParent, types } from 'mobx-state-tree';
import { hasError, isReady } from '../Fetching/updateStateHelper';

export interface ContentInput extends Partial<IContentModel> {
}


export const ContentStore = types
  .model('ContentStore', {
    content: types.array(types.maybeNull(ContentModel)),
    states: types.optional(types.array(FetchingState),[])
  })
  .actions(self => {
    const loadContent = (content: ContentInput) => {
      const transcriptVisibleMeta = 'transcriptVisible' in content? content.transcriptVisible: true;

      const contentData = {
        ...filterDefinedFields(content,ContentModel.properties),
        withTranscript: !!content.transcript,
        transcriptVisible: transcriptVisibleMeta,
        language: (getParent(self) as any).language.name.key
      };
      const index = self.content?.findIndex(c => c._id === content._id);
      if (index === -1) {
        self.content?.push(contentData);
      } else {
        self.content[index] = contentData;
      }

    };
    const fetchContent = flow(function* (id: string) {
      updateState(id, 'pending');
      try {
        const { data }: any = yield clientFunction((getParent(self) as any).language.name.key)
          .query({ query: GET_CONTENT, variables: { 'id': id }, fetchPolicy: 'no-cache' });
        if (data) {
          loadContent(data.content);
        }
        updateState(id, 'done');
      }
      catch (error) {
        updateState(id, 'error');
        captureException(error);
      }
    });

    const updateState = (id: string, newState: string)=> {
      const index = self.states.findIndex(s => s.id === id);
      if (index === -1) {
        self.states.push({ id, state: newState });
      } else {
        self.states[index].state = newState;
      }
    };

    const  setErrors = (id: [string]) => {
      id.forEach(i => setError(i));
    };

    const  setError = (id: string) => {
      const index = self.states.findIndex(s => s.id === id);
      if (index !== -1 && self.states[index].state === 'pending') {
        self.states[index].state = 'error';
      }
    };

    const fetchContentIfNotExist = flow(function* (id: string) {
      const index = self.content.findIndex(p => p._id === id);
      if (index === -1 || !self.content[index].withTranscript || self.content[index].language !== (getParent(self) as any).language.name.key) {
        yield fetchContent(id);
      }
    });
    const clear = () => {
      self.content = [];
      self.states = [];
    };
    return {
      loadContent,
      fetchContent,
      setErrors,
      setError,
      fetchContentIfNotExist,
      clear
    };
  })
  .views(self => ({
    getContent(id: string) {
      const index = self.content.findIndex(p => p._id === id);
      if (index === -1) {
        return null;
      } else {
        return self.content[index];
      }

    },
    getTracks(ids: string[]) {
      return ids.map(id => {
        const content = this.getContent(id);
        if (content) {
          return {
            audioUrl: content.fileContent,
            _id: content._id,
            title: content.title,
            transcript: content.transcript,
            transcriptVisible: content.transcriptVisible,
            humanReadableId: content.humanReadableId,
            activityType: content.activityType,
            albumArtUrl: null,
            subtitle: null
          };
        }
      });

    },

    getVideos(ids: string[]) {
      return ids.map(id => {
        const content = this.getContent(id);
        if (content) {
          return {
            videoUrl: content.fileContent,
            _id: content._id,
            title: content.title,
            transcript: content.transcript,
            transcriptVisible: content.transcriptVisible,
            albumArtUrl: null,
            subtitle: null
          };
        }
      });
    },
    hasError(id: string) {
      return hasError(self, id);
    },
    hasErrorPlural(id: string[]) {
      return id.every(i => hasError(self, i));
    },
    isReady(id: string) {
      return isReady(self, id);
    },
    isReadyPlural(id: string[]) {
      return id.every(i => isReady(self, i));
    },
  }
  ));

